import { useMemo } from "react";

import type {
  IdentifierType,
  ScreenMembersOnResetPasswordRequest,
} from "@auth0/auth0-acul-js";
import ResetPasswordRequestInstance from "@auth0/auth0-acul-js/reset-password-request";

import { executeSafely } from "../utils/helpers/executeSafely";

const getPrefilledIdentifier = (
  data: ScreenMembersOnResetPasswordRequest["data"]
): string => {
  if (!data) return "";
  return data.username || data.email || data.phone || "";
};

export const useResetPasswordRequestManager = () => {
  const resetPasswordRequestInstance = useMemo(
    () => new ResetPasswordRequestInstance(),
    []
  );

  const { screen, transaction } = resetPasswordRequestInstance;
  const { texts, captchaImage, data } = screen;
  const { allowedIdentifiers, requiredIdentifiers, hasFlexibleIdentifier } =
    transaction;

  const handleResetPassword = async (
    identifier: string,
    captcha?: string
  ): Promise<void> => {
    const options: { username: string; captcha?: string } = {
      username: identifier?.trim() || "",
    };

    if (screen.isCaptchaAvailable && captcha?.trim()) {
      options.captcha = captcha.trim();
    }

    executeSafely(
      `ResetPasswordRequest with options: ${JSON.stringify(options)}`,
      () => resetPasswordRequestInstance.resetPassword(options)
    );
  };

  const handleBackToLogin = async (): Promise<void> => {
    executeSafely("ResetPasswordRequest back to login", () =>
      resetPasswordRequestInstance.backToLogin()
    );
  };

  return {
    resetPasswordRequestInstance,
    handleResetPassword,
    handleBackToLogin,
    texts: (texts || {}) as ScreenMembersOnResetPasswordRequest["texts"],
    errors: resetPasswordRequestInstance.getError(),
    isCaptchaAvailable: screen.isCaptchaAvailable === true,
    captchaImage,
    allowedIdentifiers: (allowedIdentifiers ?? []) as IdentifierType[],
    requiredIdentifiers: (requiredIdentifiers ?? []) as IdentifierType[],
    hasFlexibleIdentifier: hasFlexibleIdentifier === true,
    prefilledIdentifier: getPrefilledIdentifier(data),
  };
};
